import React from 'react';
import ManagedLayout, {
    removeGlobalsWhenDuplicatedInUser,
} from './ManagedLayout';
import LayoutManagerGrid from './grid/LayoutManagerGrid';
import orderBy from 'lodash/orderBy';
import {
    Button,
    CheckboxInput,
    IconNames,
} from '@instinet-analytics/semantic-ui-components';
import { LayoutManagerDictionary } from 'layoutManagement/LayoutManagerDictionary';
import { PendingLayoutOverlayPanel } from 'layout/PendingLayoutOverlayPanel';
import { useSelectLayoutManualSave } from 'layout/store/selectors';
import { useDispatchSetManualLayoutSave } from 'layout/store/dispatcher';

export interface LayoutListContentProps {
    dictionary: LayoutManagerDictionary;
    shareLayoutFeatureToggleEnabled: boolean;
    layouts: ManagedLayout[];
    activeLayoutId: number;
    activeLayoutName: string;
    showGlobalsWhenDuplicatedInUser?: boolean;
    loadIcon?: IconNames;
    loadHoverIcon?: IconNames;
    isAppLayoutManager: boolean;
    onLoadClick: (layout: ManagedLayout) => void;
    onShareClick: (layout: ManagedLayout) => void;
    onDuplicateClick: (layout: ManagedLayout) => void;
    onDeleteClick: (layout: ManagedLayout) => void;
    onSaveCurrentClick?: () => void;
}

export default function LayoutListContent(props: LayoutListContentProps) {
    const {
        dictionary,
        shareLayoutFeatureToggleEnabled,
        layouts,
        activeLayoutId,
        activeLayoutName,
        isAppLayoutManager,
        loadIcon,
        loadHoverIcon,
        showGlobalsWhenDuplicatedInUser,
        onLoadClick,
        onShareClick,
        onDuplicateClick,
        onDeleteClick,
        onSaveCurrentClick,
    } = props;

    const manualSave = useSelectLayoutManualSave();
    const dispatchManualSave = useDispatchSetManualLayoutSave();

    const onManualSaveClicked = () => {
        dispatchManualSave(!manualSave);
    };

    const filteredLayouts = showGlobalsWhenDuplicatedInUser
        ? layouts
        : removeGlobalsWhenDuplicatedInUser(layouts);

    const layoutGridData = orderBy(filteredLayouts, [
        (l) => l.name.toLowerCase(),
    ]).map((x) => ({
        isActive: !x.isGlobal && x.layoutId === activeLayoutId,
        layout: x,
    }));

    return (
        <div className="LayoutListContent modal-content">
            <div className="toolbar">
                <div className="start">
                    <div className="title">
                        {dictionary.title} ({filteredLayouts.length})
                    </div>
                    <div className="subtitle">
                        Currently Active: {activeLayoutName}
                    </div>
                </div>
                <div className="end">
                    {isAppLayoutManager && (
                        <CheckboxInput
                            className="manual-save-checkbox"
                            checked={!manualSave}
                            label={'Auto Save'}
                            onToggle={onManualSaveClicked}
                        />
                    )}
                    {onSaveCurrentClick && (
                        <Button onClick={onSaveCurrentClick} inverted>
                            Save Current
                        </Button>
                    )}
                </div>
            </div>
            <PendingLayoutOverlayPanel />
            <LayoutManagerGrid
                dictionary={dictionary}
                shareLayoutFeatureToggleEnabled={
                    shareLayoutFeatureToggleEnabled
                }
                isAppLayoutManager={isAppLayoutManager}
                loadIcon={loadIcon}
                loadHoverIcon={loadHoverIcon}
                rowData={layoutGridData}
                onLoadClick={onLoadClick}
                onShareClick={onShareClick}
                onDuplicateClick={onDuplicateClick}
                onDeleteClick={onDeleteClick}
            />
        </div>
    );
}
